import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPencil, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Button, IconButton, Input } from '../../design-system';
import { updateBrokerCredential } from '../../utils/savedSecrets';
import { getErrorMessage } from '../../utils/errorMessage';
import './BrokerAccounts.css';

interface BrokerAccountRenameControlProps {
  brokerAccountId: string;
  currentName: string;
  getApiToken: () => Promise<string | null>;
  onSaved: (displayName: string) => void;
  className?: string;
}

export default function BrokerAccountRenameControl({
  brokerAccountId,
  currentName,
  getApiToken,
  onSaved,
  className,
}: BrokerAccountRenameControlProps): React.ReactElement {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(currentName);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!editing) setValue(currentName);
  }, [currentName, editing]);

  const cancel = (): void => {
    setEditing(false);
    setValue(currentName);
    setError('');
  };

  const save = async (): Promise<void> => {
    const displayName = value.trim();
    if (!displayName) {
      setError('Enter an account name.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const token = await getApiToken();
      await updateBrokerCredential(token, brokerAccountId, { displayName });
      onSaved(displayName);
      setEditing(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not rename this account.'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <IconButton
        className={className}
        label={`Rename ${currentName}`}
        onClick={() => setEditing(true)}
      >
        <FontAwesomeIcon icon={faPencil} />
      </IconButton>
    );
  }

  return (
    <div className={['broker-account-rename', className].filter(Boolean).join(' ')}>
      <div className="broker-account-rename__field">
        <Input
          aria-label="Account name"
          autoFocus
          maxLength={160}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void save();
            }
            if (event.key === 'Escape') cancel();
          }}
        />
        {error && <span className="broker-account-rename__error">{error}</span>}
      </div>
      <Button
        aria-label="Save account name"
        icon={<FontAwesomeIcon icon={faCheck} />}
        loading={saving}
        onClick={() => void save()}
        size="sm"
        variant="success"
      >
        Save
      </Button>
      <IconButton label="Cancel account rename" disabled={saving} onClick={cancel}>
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
    </div>
  );
}
